import React, { Component } from 'react';
import { CSSTransition } from './CSSTransition';
import { WayfindingComponent, WayfindingQRCode } from '@touchsource/wayfinding_lib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlayCircle } from '@fortawesome/free-solid-svg-icons';
import { condorComponentENV, envENV } from '../ENV';
import { DetailsSections } from './DetailsSection';
import { buildFeatureGroupCandidatesByMap } from './BuildingFeatures';

// Mirrors wayfinding_lib/src/lib.js encodeUUIDToBase64, which isn't re-exported.
const encodeUUIDToBase64ForQR = (uuidString) => {
	let binaryData = '';
	for (let i = 0; i < uuidString.length; i += 2) {
		binaryData += String.fromCharCode(parseInt(uuidString.substr(i, 2), 16));
	}
	return btoa(binaryData);
};

class WayfindingModal extends Component {
	constructor(props) {
		super(props);
		this.state = {
			activeButton: ''
		};
		this.closeTimer = null;
		this.modalRef = React.createRef();
		this.wayfindingRef = React.createRef();
		// Fallbacks used during the ~700ms exit transition, after the parent
		// clears its UUID state but the modal is still mounted.
		this.lastListingUUID = '';
		this.lastFeatureGroupUUID = '';
	}

	componentDidUpdate(prevProps) {
		const justOpened = !prevProps.isOpen && this.props.isOpen;
		if (justOpened && this.props.featureGroupUUID) {
			this.routeToFeatureGroup(this.props.featureGroupUUID);
		}

		if (this.props.isOpen) {
			if (this.props.listingUUID) this.lastListingUUID = this.props.listingUUID;
			if (this.props.featureGroupUUID) this.lastFeatureGroupUUID = this.props.featureGroupUUID;
		}
	}

	componentWillUnmount() {
		if (this.closeTimer) {
			clearTimeout(this.closeTimer);
		}
	}

	// Bronco nests display_name under account.<uuid> (cardinal.go) — also check
	// system_group scope and a root-level fallback in case the ETL lifts it.
	getFeatureGroupDisplayName = (featureGroupKey) => {
		const fd = window.flatData;
		if (!fd || !featureGroupKey) return '';
		for (const containerKey of Object.keys(fd)) {
			if (containerKey.startsWith('account.') || containerKey.startsWith('system_group.')) {
				const container = fd[containerKey];
				const fg = container && container[featureGroupKey];
				if (fg && fg.display_name) return fg.display_name;
			}
		}
		return fd[featureGroupKey]?.display_name || '';
	}

	// The `selectedFeatureGroup` prop only drives highlighting; routing is
	// imperative via this ref call.
	routeToFeatureGroup = (featureGroupKey) => {
		const candidatesByMap = buildFeatureGroupCandidatesByMap(window.flatData, featureGroupKey);
		if (Object.keys(candidatesByMap).length === 0) return;

		const tryRoute = () => {
			const ref = this.wayfindingRef.current;
			if (ref && typeof ref.wayfindToFeatureGroupPoint === 'function') {
				ref.wayfindToFeatureGroupPoint(candidatesByMap);
			}
		};
		// Wait one frame for the ref to populate after CSSTransition mounts.
		if (typeof requestAnimationFrame === 'function') {
			requestAnimationFrame(tryRoute);
		} else {
			setTimeout(tryRoute, 0);
		}
	}

	handleDown = (buttonId) => {
		this.setState({ activeButton: buttonId });
	}

	handleUp = () => {
		this.setState({ activeButton: '' });
	}

	labelSeparatorMap = {
		0: ' ',
		1: ': ',
		2: ' - ',
		3: '',
	}

	getSystemOrPropertyOrAccountSetting = (mapSetting, checkbox = false) => {
		const fd = window.flatData;
		if (!fd) return checkbox ? false : '';

		let settingValue = '';

		let accountKey, systemGroupKey;
		Object.keys(fd).forEach((key) => {
			if (key.startsWith('account.')) accountKey = key;
			if (key.startsWith('system_group.')) systemGroupKey = key;
		});

		const account = fd[accountKey] || {};
		const systemGroup = fd[systemGroupKey] || {};
		const systemUUID = this.getSystemUUID();
		const activeSystem = fd[`system.${systemUUID}`] || {};

		let accountSetting;
		if (mapSetting in account) {
			accountSetting = { value: account[mapSetting] ?? null };
		}

		let propertySetting;
		if (mapSetting in systemGroup) {
			propertySetting = { value: systemGroup[mapSetting] ?? null };
		}

		let systemSetting;
		if (mapSetting in activeSystem) {
			systemSetting = { value: activeSystem[mapSetting] ?? null };
		}

		if ((systemSetting?.value && !checkbox) || (checkbox && systemSetting?.value && systemSetting?.value !== false)) {
			settingValue = systemSetting.value;
		} else if ((propertySetting?.value && !checkbox) || (checkbox && propertySetting?.value && propertySetting?.value !== false)) {
			settingValue = propertySetting.value;
		} else if ((accountSetting?.value && !checkbox) || (checkbox && accountSetting?.value && accountSetting?.value !== false)) {
			settingValue = accountSetting.value;
		} else if (checkbox) {
			settingValue = false;
		} else {
			settingValue = '';
		}

		return settingValue;
	}

	getSystemGroupUUID = () => {
		if (this.props.systemGroupUUID) return this.props.systemGroupUUID;

		const fd = window.flatData;
		if (!fd) return '';

		const systemGroupKey = Object.keys(fd).find(key => key.startsWith('system_group.'));
		return systemGroupKey || '';
	}

	getSystemUUID = () => {
		if (this.props.systemUUID) return this.props.systemUUID;

		const fd = window.flatData;
		if (!fd) return '';

		const systemKey = Object.keys(fd).find(key => key.startsWith('system.'));
		return systemKey ? systemKey.split('.')[1] : '';
	}

	getNormalizedListingUUID = () => {
		let listingUUID = this.props.listingUUID;
		if (listingUUID && !listingUUID.startsWith('listing.')) {
			listingUUID = `listing.${listingUUID}`;
		}
		return listingUUID;
	}

	getWayfindingColumns = (listingUUID) => {
		const fd = window.flatData;
		let primaryWayfindingColumn = '';
		let secondaryWayfindingColumn = '';

		for (const [m1Key, m1Data] of Object.entries(fd)) {
			if (m1Key.startsWith('listing_group.')) {
				for (const [m2Key, m2Data] of Object.entries(m1Data)) {
					if (m2Key.startsWith('listing_config.')) {
						if (m2Data.wf_column && fd[listingUUID]?.[`listing_column.${m2Data.wf_column}`]) {
							primaryWayfindingColumn = m2Data.wf_column;
						}
						if (m2Data.wf_fallback_column && fd[listingUUID]?.[`listing_column.${m2Data.wf_fallback_column}`]) {
							secondaryWayfindingColumn = m2Data.wf_fallback_column;
						}
					}
				}
			}
		}

		return { primaryWayfindingColumn, secondaryWayfindingColumn };
	}

	getListingConfigUUID = (listingUUID) => {
		const fd = window.flatData;
		if (!fd || !listingUUID) return '';

		for (const [m1Key, m1Data] of Object.entries(fd)) {
			if (m1Key.startsWith('listing_config.')) {
				if (m1Data[listingUUID]) {
					return m1Key;
				}
			}
		}

		if (fd[listingUUID]) {
			for (const key of Object.keys(fd[listingUUID] || {})) {
				if (key.startsWith('listing_config.')) {
					return key;
				}
			}
		}

		return '';
	}

	getLegacyFields = (listingUUID) => {
		const fd = window.flatData;
		let legacyBuildingName = '';
		let legacyLandmarkId = '';
		let legacyLocationName = '';

		if (this.props.detailsGrp && this.props.detailsGrp.length > 0) {
			this.props.detailsGrp.forEach((columnData) => {
				if (columnData.type === 'Building') {
					legacyBuildingName = columnData.value;
				}
				if (columnData.type === 'Location (apt/suite)' && columnData.label === 'landmark_id') {
					legacyLandmarkId = columnData.value;
				}
				if (columnData.type === 'Location (apt/suite)' && columnData.label !== 'landmark_id') {
					legacyLocationName = columnData.value;
				}
			});
			return { legacyBuildingName, legacyLandmarkId, legacyLocationName };
		}

		if (!fd || !listingUUID || !fd[listingUUID]) {
			return { legacyBuildingName, legacyLandmarkId, legacyLocationName };
		}

		for (const [colKey, colData] of Object.entries(fd[listingUUID] || {})) {
			if (colKey.startsWith('listing_column.')) {
				let columnType = '';
				let columnDisplayName = '';
				const listingColumnData = fd[colKey];
				if (listingColumnData) {
					for (const m2Key of Object.keys(listingColumnData)) {
						if (m2Key.startsWith('listing_column_type.')) {
							columnType = fd[m2Key]?.display_name || '';
							columnDisplayName = listingColumnData.display_name || '';
							break;
						}
					}
				}

				if (columnType === 'Building') {
					legacyBuildingName = colData.string || '';
				}
				if (columnType === 'Location (apt/suite)') {
					if (columnDisplayName === 'landmark_id' || colKey.includes('landmark_id')) {
						legacyLandmarkId = colData.string || '';
					} else {
						legacyLocationName = colData.string || '';
					}
				}
			}
		}

		return { legacyBuildingName, legacyLandmarkId, legacyLocationName };
	}

	getListingNameCol = () => {
		if (this.props.listingNameCol) return this.props.listingNameCol;

		const detailsData = this.props.detailsGrp?.length ? this.props.detailsGrp : this.buildDetailsData();

		const nameColumn = detailsData.find(item =>
			item.type === 'Name' || item.type === 'Title'
		);

		if (nameColumn && nameColumn.column_uuid) {
			return nameColumn.column_uuid;
		}

		if (detailsData.length > 0 && detailsData[0].column_uuid) {
			return detailsData[0].column_uuid;
		}

		return '';
	}

	getWayfindingSettings = () => {
		const defaultSettings = {
			locationMarkerUse: this.getSystemOrPropertyOrAccountSetting('wf_location_marker_use', true),
			locationMarkerColor: this.getSystemOrPropertyOrAccountSetting('wf_location_marker_color'),
			locationMarkerSize: this.getSystemOrPropertyOrAccountSetting('wf_location_marker_size'),
			mapDelayIn: this.getSystemOrPropertyOrAccountSetting('wf_map_delay_in'),
			mapDelayOut: this.getSystemOrPropertyOrAccountSetting('wf_map_delay_out'),
			pathColor: this.getSystemOrPropertyOrAccountSetting('wf_path_color'),
			pathEndmarker: this.getSystemOrPropertyOrAccountSetting('wf_path_endmarker'),
			pathEndmarkerSize: this.getSystemOrPropertyOrAccountSetting('wf_path_endmarker_size'),
			pathWidth: this.getSystemOrPropertyOrAccountSetting('wf_path_width'),
			pathSpeed: this.getSystemOrPropertyOrAccountSetting('wf_path_speed'),
			pathType: this.getSystemOrPropertyOrAccountSetting('wf_path_type'),
			animIconColorBg: this.getSystemOrPropertyOrAccountSetting('wf_anim_icon_color_bg'),
			animIconColorFill: this.getSystemOrPropertyOrAccountSetting('wf_anim_icon_color_fill'),
			animIconType: this.getSystemOrPropertyOrAccountSetting('wf_anim_icon_type'),
			wayfindingZoomPan: this.getSystemOrPropertyOrAccountSetting('wf_zoom_and_pan_on', true),
			// DEV-11237: "Show Info Box" — a TLC-level setting, provided by App.js
			// via props.settings (getTLCSetting). Default off here.
			showInfoBox: false,
			// No checkbox flag, mirroring ListingsScroller: unset resolves to '' which
			// the `=== false ? false : true` coercion below turns into on-by-default.
			showMapButtons: this.getSystemOrPropertyOrAccountSetting('wf_show_map_buttons'),
			// Off by default; enabled via the TLC settings wayfinding_journey_panel /
			// wayfinding_eta passed down in props.settings from App.js (DEV-11822).
			journeyPanel: false,
			journeyEta: false,
		};

		if (this.props.settings) {
			return { ...defaultSettings, ...this.props.settings };
		}

		return defaultSettings;
	}

	// DEV-11237: the info box CTA ("Details & Wayfinding") routes the wayfinding
	// modal to the tapped listing — re-rendering the map + details for that
	// location, the kiosk equivalent of selecting a listing.
	// DEV-12172: an owner that manages this modal's listing state (Listings.js)
	// must update it in place via onListingChange — dispatching
	// openDirectWayfindingModal from there stacks App.js's separate modal
	// instance on top of this one. The dispatch fallback is only correct for the
	// App.js-owned instance, where it updates the already-open modal in place.
	handleInfoBoxOpenDetails = (matchedLocation) => {
		const raw = matchedLocation?.listingUUID || matchedLocation?.key;
		if (!raw) return;
		const listingUUID = raw.startsWith('listing.') ? raw : `listing.${raw}`;
		if (typeof this.props.onListingChange === 'function') {
			this.props.onListingChange(listingUUID);
			return;
		}
		if (typeof this.props.navEvents === 'function') {
			this.props.navEvents('openDirectWayfindingModal', listingUUID);
		}
	}

	// The lib's componentDidUpdate compares landmarkIdentifier by REFERENCE and tears
	// down/replays the whole route when it changes, so a multi-entry array must keep a
	// stable identity across the parent timer re-renders that happen while the modal
	// is open.
	parseLandmarkIdentifier = (raw) => {
		if (raw === this._lastLandmarkRaw) return this._lastParsedLandmark;
		let parsed = raw;
		try {
			const asJson = JSON.parse(raw);
			if (Array.isArray(asJson)) {
				parsed = asJson.length === 1 ? asJson[0] : asJson;
			}
		} catch (e) { /* not JSON, keep as string */ }
		this._lastLandmarkRaw = raw;
		this._lastParsedLandmark = parsed;
		return parsed;
	}

	buildWayfindingContent = () => {
		const fd = window.flatData;
		// Fall back to last-known UUIDs during the exit transition so we don't
		// flash "Listing not found" while the modal fades.
		const featureGroupUUID = this.props.featureGroupUUID || (!this.props.isOpen ? this.lastFeatureGroupUUID : '');
		const isFeatureGroupMode = !!featureGroupUUID;
		const liveListingUUID = isFeatureGroupMode ? '' : this.getNormalizedListingUUID();
		const listingUUID = liveListingUUID || (!this.props.isOpen && !isFeatureGroupMode ? this.lastListingUUID : '');

		if (!fd) return null;
		if (!isFeatureGroupMode && (!listingUUID || !fd[listingUUID])) {
			return (
				<div style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					width: '100%',
					height: '100%',
					fontSize: '1.5em',
					color: '#888'
				}}>
					Listing not found
				</div>
			);
		}

		let landmarkIdentifier = '';
		let landmarkIdentifierFallback = '';
		let primaryWayfindingColumn = '';
		let listingConfigUUID = '';
		let legacyBuildingName = '';
		let legacyLandmarkId = '';
		let legacyLocationName = '';
		let listingNameCol = '';

		if (!isFeatureGroupMode) {
			// bare uuid; the WayfindingComponent prop below must be the full
			// 'listing_column.<uuid>' flat-data key (same shape ListingsDetails
			// passes) — a bare uuid silently breaks point naming → no info box.
			const cols = this.getWayfindingColumns(listingUUID);
			primaryWayfindingColumn = cols.primaryWayfindingColumn;
			const secondaryWayfindingColumn = cols.secondaryWayfindingColumn;

			landmarkIdentifier = this.parseLandmarkIdentifier(fd[listingUUID][`listing_column.${primaryWayfindingColumn}`]?.string || '');

			landmarkIdentifierFallback = fd[listingUUID][`listing_column.${secondaryWayfindingColumn}`]?.string || '';
			listingConfigUUID = this.getListingConfigUUID(listingUUID);
			const legacy = this.getLegacyFields(listingUUID);
			legacyBuildingName = legacy.legacyBuildingName;
			legacyLandmarkId = legacy.legacyLandmarkId;
			legacyLocationName = legacy.legacyLocationName;
			listingNameCol = this.getListingNameCol();
		}

		// Destination label for the journey panel's Destination step.
		let journeyDestinationName = '';
		if (isFeatureGroupMode) {
			journeyDestinationName = this.getFeatureGroupDisplayName(featureGroupUUID);
		} else if (listingNameCol) {
			// listingNameCol is already a full flatData key (`listing_column.<uuid>`)
			// from getListingNameCol()/buildDetailsData; only prefix a bare uuid so
			// we don't double-prefix into `listing_column.listing_column.<uuid>`.
			const nameKey = listingNameCol.startsWith('listing_column.') ? listingNameCol : `listing_column.${listingNameCol}`;
			journeyDestinationName = fd[listingUUID]?.[nameKey]?.string || '';
		}

		const settings = this.getWayfindingSettings();
		const wayfindingCss = this.props.CSSModules?.['Wayfinding'] || {};

		const systemUUIDForWayfinding = this.getSystemUUID();
		const systemGroupUUIDForWayfinding = this.getSystemGroupUUID();

		const qrCodeText = this.props.qrCodeText || 'Use your camera or tablet to take this map with you';

		return (
			<>
				<WayfindingComponent
					ref={this.wayfindingRef}
					adaActive={this.props.adaActive || false}
					componentUUID={condorComponentENV}
					css={wayfindingCss}
					env={envENV}
					flatData={window.flatData}
					isMobile={false}
					landmarkIdentifier={landmarkIdentifier}
					landmarkIdentifierFallback={landmarkIdentifierFallback}
					legacyBuildingName={legacyBuildingName}
					legacyLandmarkId={legacyLandmarkId}
					legacyLocationName={legacyLocationName}
					listingConfigUUID={listingConfigUUID}
					listingNameCol={listingNameCol}
					listingUUID={listingUUID}
					locationMarkerUse={settings.locationMarkerUse}
					locationMarkerColor={settings.locationMarkerColor}
					locationMarkerSize={settings.locationMarkerSize}
					mapDelayIn={settings.mapDelayIn}
					mapDelayOut={settings.mapDelayOut}
					pathColor={settings.pathColor}
					pathEndmarker={settings.pathEndmarker}
					pathEndmarkerSize={settings.pathEndmarkerSize}
					pathWidth={settings.pathWidth}
					pathSpeed={settings.pathSpeed}
					pathType={settings.pathType}
					animIconColorBg={settings.animIconColorBg}
					animIconColorFill={settings.animIconColorFill}
					animIconType={settings.animIconType}
					primaryWayfindingColumn={primaryWayfindingColumn ? `listing_column.${primaryWayfindingColumn}` : ''}
					qrCodeText={qrCodeText}
					selectedFeatureGroup={featureGroupUUID || null}
					showMapButtons={settings.showMapButtons === false ? false : true}
					systemUUID={systemUUIDForWayfinding}
					systemGroupUUID={systemGroupUUIDForWayfinding}
					wayfindingZoomPan={settings.wayfindingZoomPan}
					/* truthy, not === true: bronco serializes bools as JSON booleans but
					   the Spark preview path can inject 1/"true" (same convention as
					   wayfindingZoomPan and the other settings in this pipeline) */
					showJourneyPanel={!!settings.journeyPanel}
					showJourneyEta={!!settings.journeyEta}
					/* Modal layout: journey steps docked left (25%), map right (75%).
					   The inline ListingsDetails surface keeps the overlay default. */
					journeyPanelPosition="left"
					journeyDestinationName={journeyDestinationName}
					showInfoBox={settings.showInfoBox}
					onInfoBoxOpenDetails={this.handleInfoBoxOpenDetails}
					infoBoxScale={0.7}
					/* The lib's buildQRCodeURL returns '' without a listingUUID;
					   we render our own feature-group QR below instead. */
					hideQRCode={isFeatureGroupMode || undefined}
				/>
				{isFeatureGroupMode && this.buildFeatureGroupQRCode(featureGroupUUID, qrCodeText, wayfindingCss)}
			</>
		);
	}

	// Mirrors the lib's buildQRCodeURL but emits `&fg=<bare-uuid>` instead of
	// `&l=`/`&lid=`. condor_mobile reads `fg` to pre-select on load.
	buildFeatureGroupQRCode = (featureGroupKey, qrCodeText, wayfindingCss) => {
		if (!featureGroupKey) return null;
		const featureGroupBareUUID = featureGroupKey.split('.')[1] || '';
		if (!featureGroupBareUUID) return null;

		let subdomain = 'web';
		if (envENV && envENV !== 'ciprod') {
			subdomain = `web-${envENV}`;
		}

		const encodedSystemUUID = encodeUUIDToBase64ForQR((condorComponentENV || '').replace(/-/g, ''));
		const systemSection = `s=${btoa(encodedSystemUUID)}`;
		const featureGroupSection = `&fg=${featureGroupBareUUID}`;

		const qrCodeURL = `http://${subdomain}.touchsource.com/?${systemSection}${featureGroupSection}`;

		// These mirror the values computed in buildWayfindingContent; recompute
		// them here so this method stays self-contained (it's called directly,
		// not closed over that scope).
		const settings = this.getWayfindingSettings();
		const systemUUIDForWayfinding = this.getSystemUUID();
		const systemGroupUUIDForWayfinding = this.getSystemGroupUUID();

		return (
			<WayfindingQRCode
				css={wayfindingCss || {}}
				qrCodeURL={qrCodeURL}
				qrCodeText={qrCodeText}
				systemUUID={systemUUIDForWayfinding}
				systemGroupUUID={systemGroupUUIDForWayfinding}
				wayfindingZoomPan={settings.wayfindingZoomPan}
			/>
		);
	}

	buildDetailsData = () => {
		const fd = window.flatData;
		const listingUUID = this.getNormalizedListingUUID();

		if (!listingUUID || !fd || !fd[listingUUID]) {
			return [];
		}

		const details = [];
		const listingData = fd[listingUUID];

		const getColumnType = (listingColumnData) => {
			if (!listingColumnData) return '';
			for (const key of Object.keys(listingColumnData)) {
				if (key.startsWith('listing_column_type.')) {
					return fd[key]?.display_name || '';
				}
			}
			return '';
		};

		for (const [colKey, colData] of Object.entries(listingData || {})) {
			if (colKey.startsWith('listing_column.')) {
				const listingColumnData = fd[colKey];
				const columnType = getColumnType(listingColumnData);
				const displayName = listingColumnData?.display_name || colKey.split('.')[1];

				const keys = Object.keys(colData);
				let value = keys.length > 0 ? colData[keys[0]] : '';

				if (typeof value === 'number') {
					value = String(value);
				}

				if (value) {
					details.push({
						value: value,
						type: columnType,
						label: displayName,
						column_uuid: colKey,
					});
				}
			}
		}

		return details;
	}

	getListingTier = (listingUUID) => {
		const fd = window.flatData;
		if (!fd || !listingUUID) return 0;

		let tier = 0;
		let currentKey = listingUUID;

		while (currentKey) {
			for (const [key, data] of Object.entries(fd)) {
				if (key.startsWith('listing_collection.') && data[currentKey]) {
					return tier;
				}
			}

			let parentFound = false;
			for (const [key, data] of Object.entries(fd)) {
				if (key.startsWith('listing.') && data[currentKey]) {
					currentKey = key;
					tier++;
					parentFound = true;
					break;
				}
			}

			if (!parentFound) break;
		}

		return tier;
	}

	getListingCollection = (listingUUID) => {
		const fd = window.flatData;
		if (!fd || !listingUUID) return null;

		let currentKey = listingUUID;

		while (currentKey) {
			for (const [collectionKey, collectionData] of Object.entries(fd)) {
				if (collectionKey.startsWith('listing_collection.') && collectionData[currentKey]) {
					return collectionKey;
				}
			}

			let parentFound = false;
			for (const [key, data] of Object.entries(fd)) {
				if (key.startsWith('listing.') && data[currentKey]) {
					currentKey = key;
					parentFound = true;
					break;
				}
			}

			if (!parentFound) break;
		}

		return null;
	}

	getListingConfigForTier = (collectionKey, tier) => {
		const fd = window.flatData;
		if (!fd || !collectionKey) return null;

		const collectionData = fd[collectionKey];
		if (!collectionData) return null;

		let configKey = null;
		for (const key of Object.keys(collectionData)) {
			if (key.startsWith('listing_config.')) {
				configKey = key;
				break;
			}
		}

		if (!configKey) return null;
		if (tier === 0) return configKey;

		let currentConfig = fd[configKey];
		for (let i = 0; i < tier; i++) {
			let childConfigKey = null;
			for (const key of Object.keys(currentConfig || {})) {
				if (key.startsWith('listing_config.')) {
					childConfigKey = key;
					break;
				}
			}

			if (!childConfigKey) return configKey;
			configKey = childConfigKey;
			currentConfig = fd[configKey];
		}

		return configKey;
	}

	getDetailHeaderSettings = () => {
		const fd = window.flatData;
		const listingUUID = this.getNormalizedListingUUID();

		let detailHeaders = true;
		let labelSeparatorIndex = 1;

		if (!fd || !listingUUID) {
			return {
				showHeaders: detailHeaders,
				labelSeparator: this.labelSeparatorMap[labelSeparatorIndex] || ': ',
			};
		}

		const listingTier = this.getListingTier(listingUUID);
		const collectionKey = this.getListingCollection(listingUUID);

		if (!collectionKey) {
			return {
				showHeaders: detailHeaders,
				labelSeparator: this.labelSeparatorMap[labelSeparatorIndex] || ': ',
			};
		}

		const listingConfigKey = this.getListingConfigForTier(collectionKey, listingTier);

		if (!listingConfigKey) {
			return {
				showHeaders: detailHeaders,
				labelSeparator: this.labelSeparatorMap[labelSeparatorIndex] || ': ',
			};
		}

		const matchingGroups = [];
		for (const [listingGroupKey, listingGroupData] of Object.entries(fd)) {
			if (!listingGroupKey.startsWith('listing_group.')) continue;

			if (listingGroupData[listingConfigKey]) {
				const configData = listingGroupData[listingConfigKey];
				matchingGroups.push({
					detail_headers: configData.detail_headers,
					label_seperator: configData.label_seperator
				});
			}
		}

		if (matchingGroups.length > 0) {
			const selectedGroup = matchingGroups.find(g => g.detail_headers === true) || matchingGroups[matchingGroups.length - 1];

			if (typeof selectedGroup.detail_headers !== 'undefined') {
				detailHeaders = selectedGroup.detail_headers;
			}
			if (typeof selectedGroup.label_seperator !== 'undefined') {
				labelSeparatorIndex = selectedGroup.label_seperator;
			}

			return {
				showHeaders: detailHeaders,
				labelSeparator: this.labelSeparatorMap[labelSeparatorIndex] || ': ',
			};
		}

		return {
			showHeaders: detailHeaders,
			labelSeparator: this.labelSeparatorMap[labelSeparatorIndex] || ': ',
		};
	}

	handleRefresh = () => {
		if (this.wayfindingRef.current && this.wayfindingRef.current.reloadImage) {
			this.wayfindingRef.current.reloadImage();
		}
		if (this.props.featureGroupUUID) {
			this.routeToFeatureGroup(this.props.featureGroupUUID);
		}
	}

	buildDetailsSection = () => {
		const listingsDetailsCss = this.props.CSSModules?.['ListingsDetails'] || {};
		// DEV-12172: an empty parent-supplied detailsGrp (info box CTA target not
		// found in window.completeListings) falls back to building from flatData
		// instead of rendering an empty details panel.
		const usingParentDetails = !!this.props.detailsGrp?.length;
		const detailsData = usingParentDetails ? this.props.detailsGrp : this.buildDetailsData();

		if (detailsData.length === 0) {
			return null;
		}

		let showHeaders, labelSeparator;

		// DEV-12172: the parent's headerSettings describe the directory it owns, so
		// they only apply to details the parent supplied. Self-built details can be
		// for a listing outside that directory (info box CTA), so derive headers
		// from the live listingUUID instead of inheriting the directory's.
		if (usingParentDetails && this.props.headerSettings) {
			showHeaders = this.props.headerSettings.showHeaders;
			labelSeparator = this.props.headerSettings.labelSeparator;
		} else {
			const headerSettings = this.getDetailHeaderSettings();
			showHeaders = headerSettings.showHeaders;
			labelSeparator = headerSettings.labelSeparator;
		}

		return (
			<DetailsSections
				detailsData={detailsData}
				css={listingsDetailsCss}
				detailsUUID="wayfindingModal"
				config={{
					showHeaders: showHeaders,
					labelSeparator: labelSeparator,
					disableInteractiveButtons: true
				}}
				handlers={{
					navEvents: this.props.navEvents
				}}
				activeButton={this.state.activeButton}
				wrapperClassName={`${listingsDetailsCss.wayfindingModalDetailsSection || ''} wayfindingModalDetailsSection`}
			/>
		);
	}

	render() {
		const { isOpen, onClose, CSSModules } = this.props;
		const wayfindingModalCss = CSSModules?.['WayfindingModal'] || {};
		const listingsCss = CSSModules?.['Listings'] || {};
		const effectiveFeatureGroupUUID = this.props.featureGroupUUID || (!isOpen ? this.lastFeatureGroupUUID : '');
		const isFeatureGroupMode = !!effectiveFeatureGroupUUID;
		const isDirectMode = !isFeatureGroupMode && !this.props.detailsGrp;
		const isJourneyPanelActive = !!this.getWayfindingSettings().journeyPanel;

		const css = wayfindingModalCss.wayfindingModal ? wayfindingModalCss : listingsCss;

		const transitionClassNames = listingsCss.contentFadeContainer
			? `${listingsCss.contentFadeContainer} contentFade`
			: 'contentFade';

		let modalClassName;
		if (isFeatureGroupMode) {
			modalClassName = `wayfindingModal featureWayfindingModal ${css.featureWayfindingModal || css.directWayfindingModal || ''}`;
		} else if (isDirectMode) {
			modalClassName = `wayfindingModal directWayfindingModal ${css.directWayfindingModal || ''}`;
		} else {
			modalClassName = `wayfindingModal wayfindingModalListings ${css.wayfindingModal || ''}`;
		}

		return (
			<CSSTransition
				in={isOpen}
				appear={isOpen}
				timeout={700}
				unmountOnExit
				classNames={transitionClassNames}
				nodeRef={this.modalRef}
			>
				<div ref={this.modalRef} className={modalClassName}>
					<div className={`wayfindingModalContent ${css.wayfindingModalContent || ''}`}>
						<div className={`wayfindingModalBody ${css.wayfindingModalBody || ''}`}>
							{!isFeatureGroupMode && (
								<div className={`wayfindingModalDetails ${css.wayfindingModalDetails || ''} ${isJourneyPanelActive ? 'journeyPanelActive' : ''}`}>
									{this.buildDetailsSection()}
								</div>
							)}
							{isFeatureGroupMode && (
								<div className={`wayfindingModalFeatureGroupLabel ${css.wayfindingModalFeatureGroupLabel || ''}`}>
									{this.getFeatureGroupDisplayName(effectiveFeatureGroupUUID)}
								</div>
							)}
							<div className={`wayfindingModalMap ${css.wayfindingModalMap || ''}`}>
								{this.buildWayfindingContent()}
							</div>
						</div>
						<div className={`wayfindingModalButtons ${css.wayfindingModalButtons || ''}`}>
							<div
								className={`wayfindingModalReplayBtn ${css.wayfindingModalReplayBtn || ''} ${this.state.activeButton === 'refresh' ? `${css.activeButton || ''} activeButton` : ''}`}
								onMouseDown={(e) => {
									e.stopPropagation();
									this.handleDown('refresh');
									this.props.navEvents('btnDebouncer', this.handleRefresh);
								}}
								onMouseUp={this.handleUp}
								onMouseLeave={this.handleUp}
								onTouchStart={(e) => {
									e.stopPropagation();
									this.handleDown('refresh');
									this.props.navEvents('btnDebouncer', this.handleRefresh);
								}}
								onTouchEnd={this.handleUp}
								onPointerDown={(e) => {
									e.stopPropagation();
									this.handleDown('refresh');
									this.props.navEvents('btnDebouncer', this.handleRefresh);
								}}
								onPointerUp={this.handleUp}
								onPointerLeave={this.handleUp}
							>
								<FontAwesomeIcon icon={faPlayCircle} /> Replay
							</div>
							<div
								className={`wayfindingModalCloseBtn ${css.wayfindingModalCloseBtn || ''} ${this.state.activeButton === 'close' ? `${css.activeButton || ''} activeButton` : ''}`}
								onMouseDown={(e) => {
									e.stopPropagation();
									this.handleDown('close');
									this.props.navEvents('btnDebouncer', onClose);
								}}
								onMouseUp={this.handleUp}
								onMouseLeave={this.handleUp}
								onTouchStart={(e) => {
									e.stopPropagation();
									this.handleDown('close');
									this.props.navEvents('btnDebouncer', onClose);
								}}
								onTouchEnd={this.handleUp}
								onPointerDown={(e) => {
									e.stopPropagation();
									this.handleDown('close');
									this.props.navEvents('btnDebouncer', onClose);
								}}
								onPointerUp={this.handleUp}
								onPointerLeave={this.handleUp}
							>
								Close
							</div>
						</div>
					</div>
				</div>
			</CSSTransition>
		);
	}
}

export default WayfindingModal;
